/**
 * PostHog event names for the mobile app.
 *
 * These constants are the single source of truth for the event names. Call sites must import them
 * instead of hardcoding the strings so the code and PostHog can never drift apart — the same
 * reasoning as `featureFlags/flags.ts`, which this file mirrors.
 *
 * `trackEvent` and `captureEvent` accept only {@link AnalyticsEventName}, so a string literal or a
 * template expression at a call site is a compile error. That is what keeps every name greppable.
 *
 * Convention for new events: `object_verb`, past tense, snake_case; "asset" not "tree"; an
 * operation that can fail gets the full `*_attempted` / success / `*_failed` triad. Existing names
 * that break it are deliberately not renamed — renaming splits an event's history and breaks saved
 * insights — and are listed as known debt in the catalogue.
 *
 * @see docs/qa/system-spec.md § Analytics event catalogue — when each event fires and its properties.
 */

// Authentication

export const EVENT_USER_LOGIN_ATTEMPTED = 'user_login_attempted';
export const EVENT_USER_LOGIN_COMPLETED = 'user_login_completed';
export const EVENT_USER_LOGIN_FAILED = 'user_login_failed';
export const EVENT_USER_LOGOUT = 'user_logout';

// Home

export const EVENT_HOME_WORK_MAP_CLICKED = 'home_work_map_clicked';
export const EVENT_HOME_INVENTORY_MAP_CLICKED = 'home_inventory_map_clicked';

// Settings

export const EVENT_HAPTIC_FEEDBACK_TOGGLED = 'haptic_feedback_toggled';

// Asset creation

/** Duplicates {@link EVENT_INVENTORY_FORM_OPENED} — both fire when the new-asset screen opens. */
export const EVENT_NEW_TREE_SCREEN_OPENED = 'new_tree_screen_opened';
export const EVENT_INVENTORY_FORM_OPENED = 'inventory_form_opened';
export const EVENT_NEW_TREE_SCREEN_CLOSED_WITHOUT_SAVING = 'new_tree_screen_closed_without_saving';
export const EVENT_ASSET_CREATION_ATTEMPTED = 'asset_creation_attempted';
/** Emitted from both the offline-optimistic and the canonical-id paths. */
export const EVENT_ASSET_CREATED = 'asset_created';
export const EVENT_ASSET_CREATION_FAILED = 'asset_creation_failed';

// Asset editing

export const EVENT_ASSET_SAVE_ATTEMPTED = 'asset_save_attempted';
export const EVENT_ASSET_SAVED = 'asset_saved';
export const EVENT_ASSET_SAVE_FAILED = 'asset_save_failed';
export const EVENT_LOAD_LAST_DIALOG_OPENED = 'load_last_dialog_opened';
export const EVENT_LAST_ASSET_LOADED = 'last_asset_loaded';
export const EVENT_TREE_DETAILS_SECTION_TAPPED = 'tree_details_section_tapped';
export const EVENT_ASSET_RADIAL_MENU_ACTION_TAPPED = 'asset_radial_menu_action_tapped';

// Inspections

export const EVENT_INSPECTION_PICKER_OPENED = 'inspection_picker_opened';
export const EVENT_INSPECTION_FORM_SELECTED = 'inspection_form_selected';
export const EVENT_INSPECTION_FORM_OPENED = 'inspection_form_opened';
export const EVENT_INSPECTION_SUBMIT_ATTEMPTED = 'inspection_submit_attempted';
export const EVENT_INSPECTION_SUBMITTED = 'inspection_submitted';
/** Also emitted from the mutation handler, for failures surfacing after the component unmounts. */
export const EVENT_INSPECTION_SUBMIT_FAILED = 'inspection_submit_failed';

// Photos
//
// Spelled out rather than built from an entity prefix: a derived name is invisible to grep, and
// these were live in PostHog while appearing nowhere in the codebase.

export const EVENT_ASSET_PHOTO_ADDED = 'asset_photo_added';
export const EVENT_ASSET_PHOTO_REMOVED = 'asset_photo_removed';
export const EVENT_ASSET_PHOTO_LIMIT_REACHED = 'asset_photo_limit_reached';
export const EVENT_ASSET_PHOTO_UPLOAD_FAILED = 'asset_photo_upload_failed';
export const EVENT_INSPECTION_PHOTO_ADDED = 'inspection_photo_added';
export const EVENT_INSPECTION_PHOTO_REMOVED = 'inspection_photo_removed';
export const EVENT_INSPECTION_PHOTO_LIMIT_REACHED = 'inspection_photo_limit_reached';
export const EVENT_INSPECTION_PHOTO_UPLOAD_FAILED = 'inspection_photo_upload_failed';

// Multi-stem DBH

export const EVENT_MULTI_STEM_EDITOR_OPENED = 'multi_stem_editor_opened';
export const EVENT_MULTI_STEM_STEM_REMOVED = 'multi_stem_stem_removed';
export const EVENT_MULTI_STEM_CAP_REACHED = 'multi_stem_cap_reached';
export const EVENT_MULTI_STEM_EDITOR_ABANDONED = 'multi_stem_editor_abandoned';
export const EVENT_MULTI_STEM_SUBMITTED = 'multi_stem_submitted';

// Map view controls

export const EVENT_MAP_VIEW_CONTROLS_OPENED = 'map_view_controls_opened';
export const EVENT_MAP_VIEW_CONTROLS_APPLIED = 'map_view_controls_applied';
export const EVENT_MAP_VIEW_CONTROLS_CLEARED = 'map_view_controls_cleared';
/** Drawer closed without applying or clearing. */
export const EVENT_MAP_VIEW_CONTROLS_ABANDONED = 'map_view_controls_abandoned';
export const EVENT_MAP_DISPLAY_BY_SELECTED = 'map_display_by_selected';
export const EVENT_MAP_SHOW_ALL_TREES_TOGGLED = 'map_show_all_trees_toggled';
export const EVENT_MAP_FOLLOW_LOCATION_TOGGLED = 'map_follow_location_toggled';

// Work management

export const EVENT_WORK_LIST_FILTERS_APPLIED = 'work_list_filters_applied';
export const EVENT_WORK_TASK_COMPLETED = 'work_task_completed';
export const EVENT_WORK_TASK_RESTARTED = 'work_task_restarted';
export const EVENT_WORK_TASK_PHOTO_ADDED = 'work_task_photo_added';
export const EVENT_WORK_ORDER_FILTER_APPLIED = 'work_order_filter_applied';

// Retiring
//
// Emitted only from the legacy form and photo screens, which are being removed. Delete this block
// together with those screens. The photo names below are already dark in PostHog — the
// schema-driven screens emit `asset_photo_*` / `inspection_photo_*` instead.

/**
 * @deprecated Emitted only from the legacy screens, so it stops firing entirely once they are
 * removed — the schema-driven screens have no equivalent emitter.
 */
export const EVENT_FORM_TAB_CHANGED = 'form_tab_changed';

/** @deprecated Superseded by {@link EVENT_ASSET_PHOTO_LIMIT_REACHED}. */
export const EVENT_PHOTO_LIMIT_REACHED = 'photo_limit_reached';
/** @deprecated No counterpart on the schema-driven screens. */
export const EVENT_PHOTO_UPLOAD_STARTED = 'photo_upload_started';
/** @deprecated Superseded by {@link EVENT_ASSET_PHOTO_ADDED}. */
export const EVENT_PHOTO_UPLOADED = 'photo_uploaded';
/** @deprecated Superseded by {@link EVENT_ASSET_PHOTO_UPLOAD_FAILED}. */
export const EVENT_PHOTO_UPLOAD_FAILED = 'photo_upload_failed';
/** @deprecated Superseded by {@link EVENT_ASSET_PHOTO_REMOVED}. */
export const EVENT_PHOTO_REMOVED = 'photo_removed';
/** @deprecated No counterpart on the schema-driven screens. */
export const EVENT_PHOTO_STORED_OFFLINE = 'photo_stored_offline';
/** @deprecated No counterpart on the schema-driven screens. */
export const EVENT_PHOTO_SYNCED_FROM_OFFLINE = 'photo_synced_from_offline';

/**
 * Every event name the app may emit. {@link AnalyticsEventName} is derived from this list, so a
 * constant missing from it is not emittable.
 */
export const ANALYTICS_EVENTS = [
  EVENT_USER_LOGIN_ATTEMPTED,
  EVENT_USER_LOGIN_COMPLETED,
  EVENT_USER_LOGIN_FAILED,
  EVENT_USER_LOGOUT,
  EVENT_HOME_WORK_MAP_CLICKED,
  EVENT_HOME_INVENTORY_MAP_CLICKED,
  EVENT_HAPTIC_FEEDBACK_TOGGLED,
  EVENT_NEW_TREE_SCREEN_OPENED,
  EVENT_INVENTORY_FORM_OPENED,
  EVENT_NEW_TREE_SCREEN_CLOSED_WITHOUT_SAVING,
  EVENT_ASSET_CREATION_ATTEMPTED,
  EVENT_MULTI_STEM_EDITOR_OPENED,
  EVENT_MULTI_STEM_STEM_REMOVED,
  EVENT_MULTI_STEM_CAP_REACHED,
  EVENT_MULTI_STEM_SUBMITTED,
  EVENT_MULTI_STEM_EDITOR_ABANDONED,
  EVENT_ASSET_CREATED,
  EVENT_ASSET_CREATION_FAILED,
  EVENT_ASSET_SAVE_ATTEMPTED,
  EVENT_ASSET_SAVED,
  EVENT_ASSET_SAVE_FAILED,
  EVENT_LOAD_LAST_DIALOG_OPENED,
  EVENT_LAST_ASSET_LOADED,
  EVENT_TREE_DETAILS_SECTION_TAPPED,
  EVENT_ASSET_RADIAL_MENU_ACTION_TAPPED,
  EVENT_INSPECTION_PICKER_OPENED,
  EVENT_INSPECTION_FORM_SELECTED,
  EVENT_INSPECTION_FORM_OPENED,
  EVENT_INSPECTION_SUBMIT_ATTEMPTED,
  EVENT_INSPECTION_SUBMITTED,
  EVENT_INSPECTION_SUBMIT_FAILED,
  EVENT_ASSET_PHOTO_ADDED,
  EVENT_ASSET_PHOTO_REMOVED,
  EVENT_ASSET_PHOTO_LIMIT_REACHED,
  EVENT_ASSET_PHOTO_UPLOAD_FAILED,
  EVENT_INSPECTION_PHOTO_ADDED,
  EVENT_INSPECTION_PHOTO_REMOVED,
  EVENT_INSPECTION_PHOTO_LIMIT_REACHED,
  EVENT_INSPECTION_PHOTO_UPLOAD_FAILED,
  EVENT_MAP_VIEW_CONTROLS_OPENED,
  EVENT_MAP_VIEW_CONTROLS_APPLIED,
  EVENT_MAP_VIEW_CONTROLS_CLEARED,
  EVENT_MAP_VIEW_CONTROLS_ABANDONED,
  EVENT_MAP_DISPLAY_BY_SELECTED,
  EVENT_MAP_SHOW_ALL_TREES_TOGGLED,
  EVENT_MAP_FOLLOW_LOCATION_TOGGLED,
  EVENT_WORK_LIST_FILTERS_APPLIED,
  EVENT_WORK_TASK_COMPLETED,
  EVENT_WORK_TASK_RESTARTED,
  EVENT_WORK_TASK_PHOTO_ADDED,
  EVENT_WORK_ORDER_FILTER_APPLIED,
  // Retiring — remove these together with the legacy screens.
  EVENT_FORM_TAB_CHANGED,
  EVENT_PHOTO_LIMIT_REACHED,
  EVENT_PHOTO_UPLOAD_STARTED,
  EVENT_PHOTO_UPLOADED,
  EVENT_PHOTO_UPLOAD_FAILED,
  EVENT_PHOTO_REMOVED,
  EVENT_PHOTO_STORED_OFFLINE,
  EVENT_PHOTO_SYNCED_FROM_OFFLINE,
] as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[number];

/**
 * The photo event names a `PhotoComposeSection` emits. Passed in by each consumer rather than
 * derived from a prefix, so every name stays a literal here.
 */
export type PhotoEventNames = {
  added: AnalyticsEventName;
  removed: AnalyticsEventName;
  limitReached: AnalyticsEventName;
  uploadFailed: AnalyticsEventName;
};

export const ASSET_PHOTO_EVENTS: PhotoEventNames = {
  added: EVENT_ASSET_PHOTO_ADDED,
  removed: EVENT_ASSET_PHOTO_REMOVED,
  limitReached: EVENT_ASSET_PHOTO_LIMIT_REACHED,
  uploadFailed: EVENT_ASSET_PHOTO_UPLOAD_FAILED,
};

export const INSPECTION_PHOTO_EVENTS: PhotoEventNames = {
  added: EVENT_INSPECTION_PHOTO_ADDED,
  removed: EVENT_INSPECTION_PHOTO_REMOVED,
  limitReached: EVENT_INSPECTION_PHOTO_LIMIT_REACHED,
  uploadFailed: EVENT_INSPECTION_PHOTO_UPLOAD_FAILED,
};
